<template>
  <div
    v-if="player"
    class="mt-6 rounded-2xl border border-ink/10 bg-white/70 p-5 sm:p-6"
    data-testid="player-card"
  >
    <div
      class="flex flex-col items-center gap-5 text-center md:flex-row md:items-center md:text-left"
    >
      <div
        class="flex aspect-square w-36 flex-shrink-0 items-center justify-center overflow-hidden border border-ink/10 bg-white shadow-sm md:w-48"
      >
        <img
          v-if="player.photo"
          :src="player.photo"
          :alt="playerPhotoAlt"
          class="h-full w-full object-cover"
          loading="lazy"
          data-testid="player-photo"
        />
        <div
          v-else
          class="flex h-full w-full items-center justify-center bg-amber-50 text-2xl font-semibold text-neutral-900/70"
          data-testid="player-photo-fallback"
        >
          {{ playerInitials }}
        </div>
      </div>
      <div class="w-full">
        <p
          class="mt-2 text-2xl font-semibold text-neutral-900"
          data-testid="player-name"
        >
          {{ player.name || "-" }}
        </p>
        <dl class="mt-4 grid gap-4">
          <div v-for="field in playerInfo" :key="field.key">
            <dt
              class="text-[0.65rem] uppercase tracking-[0.2em] text-slate-500"
            >
              {{ field.label }}
            </dt>
            <dd
              class="mt-1 text-base font-semibold text-neutral-900"
              :data-testid="`player-info-${field.key}`"
            >
              {{ formatInfoValue(field.value) }}
            </dd>
          </div>
        </dl>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed } from "vue";
import { useText } from "../composables/useText.js";

const { t } = useText();

const props = defineProps({
  player: {
    type: Object,
    default: null,
  },
});

const toBirthYear = (birthDate) => {
  if (!birthDate) return null;
  const [year] = birthDate.split("-");
  return year || null;
};

const playerInfo = computed(() => [
  {
    key: "birthYear",
    label: t('common.birth-year'),
    value: toBirthYear(props.player?.birthDate),
  },
  {
    key: "nationality",
    label: t('common.nationality'),
    value: props.player?.nationality,
  },
  { key: "position", label: t('common.position'), value: props.player?.position },
]);

const formatInfoValue = (value) => {
  if (value === null || value === undefined || value === "") return "-";
  return value;
};

const playerPhotoAlt = computed(() => {
  if (!props.player?.name) return t('common.player-portrait-alt');
  return `${props.player.name} ${t('common.player-portrait-suffix')}`;
});

const playerInitials = computed(() => {
  const name = props.player?.name || "";
  const parts = name.split(" ").filter(Boolean);
  const initials = parts
    .slice(0, 2)
    .map((part) => part[0])
    .join("");
  return (initials || "?").toUpperCase();
});
</script>
